// Pure helpers for what the lab report inbox shows (TGCLOUD-1866, Phase 3). No hooks: anything that
// renders a date takes the screen's formatter (`fmtDate`, from useDateFormatter) as a parameter, so
// there is one date format in the app (CLAUDE.md, Dates).
//
// THE DATES HERE, BY HOW THEY ARE WRITTEN:
//   receivedAt, decision `at`, match `at`, resolution `at`    server instants  toLocalDateFromInstant
//   summary.reportDate, a printed DOB's readings, collection  calendar dates    fmtDate (toLocalDate)
//   a record's dob / dod                                       stored dates      fmtDate (toLocalDate)
//   a Test's dateRequested / dateResultReceived                picker OR import  toLocalDateFromPickerOrImport
// A printed DOB is shown AS PRINTED, verbatim: which way round it reads is the question.
//
// THE WORDS ARE IN THE SELECTED LANGUAGE (TGCLOUD-1893): each sentence is one key for translate(),
// with the record's and the report's values filled in after, never sent to it. English is exactly
// what it was.
import { CONSTANTS } from 'src/components/Constants';
import { translate } from 'src/hooks/useTranslate';
import {
  toLocalDateFromInstant,
  toLocalDateFromPickerOrImport
} from 'src/util/partialDate';
import {
  EVIDENCE_DETAIL_LABELS,
  HEALTHCARE_ID_SCHEME_LABELS,
  INDIVIDUAL_ROLE_LABELS,
  RELATIONSHIP_RECORD_LABELS,
  RELATIONSHIP_STATED_LABELS,
  REPORT_KEY_LABELS,
  VERIFICATION_METHOD_LABELS,
  labelOf
} from './labReportInboxVocabulary';

const hasText = (v) => typeof v === 'string' && v.trim() !== '';

/** The patient's name as the report prints it (the first one), or ''. */
export function printedNameOf(reportIdentity) {
  const name = reportIdentity?.names?.[0];
  if (!name) return '';
  if (hasText(name.asPrinted)) return name.asPrinted;
  return [...(name.given || []), name.surname].filter(hasText).join(' ');
}

/** A person's name as recorded, or a placeholder that says there is none. */
export const personName = (p) =>
  [p?.firstName, p?.lastName].filter(hasText).join(' ') ||
  translate('Name not recorded');

/** The recorded sex as a word. */
export function sexText(gender) {
  const value = String(gender || '').trim();
  if (!value) return '';
  const word = value.charAt(0).toUpperCase() + value.slice(1);
  return translate(word);
}

/** An instant the server wrote, as the date (and, with `withTime`, the time) it was here. */
export function instantText(value, fmtDate, { withTime = false } = {}) {
  const local = toLocalDateFromInstant(value);
  if (!local) return '';
  const day = fmtDate(local);
  if (!withTime) return day;
  const pad = (n) => String(n).padStart(2, '0');
  return `${day} ${pad(local.getHours())}:${pad(local.getMinutes())}`;
}

/** A Test date written by the form's picker or by an importer (CLAUDE.md, Dates). */
export const testDateText = (value, fmtDate) => {
  const local = toLocalDateFromPickerOrImport(value);
  return local ? fmtDate(local) : '';
};

/** Who made a decision: the log keeps a user id, never a name. */
export const byText = (by, me) =>
  by && me && String(by) === String(me)
    ? translate('you')
    : translate('another user');

/** The identity parts the matcher set aside because too many people share them, in a sentence. */
export function tooCommonText(keys = []) {
  const words = {
    'national-id': 'the Healthcare ID',
    'hospital-number': 'the hospital number',
    dob: 'the date of birth',
    surname: 'the surname'
  };
  return translate(
    'Not used, because more than 20 people in this group share it: {{parts}}.',
    {
      parts: keys.map((k) => (words[k] ? translate(words[k]) : k)).join(', ')
    }
  );
}

/** How a match was verified, in a sentence (a match's `verification`). */
export function matchVerificationText(verification) {
  const v = verification || {};
  if (v.comparedWithReport)
    return translate('The identifier was compared with the report.');
  if (v.method) {
    return translate('Verified: {{method}}.', {
      method: labelOf(VERIFICATION_METHOD_LABELS, v.method).toLowerCase()
    });
  }
  return translate('Matched by uploading the report on the patient’s Test.');
}

/**
 * The deep link the hand-off opens (plan, "Hand-off to Phase 2"): the matched person's pedigree
 * with their Test open and the report waiting to be read into it. The router state selects the
 * person (`selectedFamilyMemberId`): without it the Test opens with nobody selected, which
 * switches the lab-report parts off.
 *
 * ONE PERSON OF A REPORT ABOUT SEVERAL (TGCLOUD-1868, "Phase E contracts, in detail for the build":
 * "The deep link adds `&individual=<key>&pregnancyId=&fetusId=`"). `individual` names which of the
 * report's people the Test is for; a fetus adds the fetus record its result belongs to, on its
 * mother's record. The person in the link and the router state is then the mother. A report about
 * one person adds nothing, so its link is exactly as it was.
 */
export function handOffLocation({
  familyId,
  familyMemberId,
  testId,
  inboxItemId,
  individualKey,
  pregnancyId,
  fetusId
}) {
  const query = new URLSearchParams({
    familyMemberId,
    testId,
    labReportInboxItem: inboxItemId
  });
  if (hasText(individualKey)) {
    query.set('individual', individualKey);
    if (hasText(pregnancyId) && hasText(fetusId)) {
      query.set('pregnancyId', pregnancyId);
      query.set('fetusId', fetusId);
    }
  }
  return {
    pathname: CONSTANTS.pedigreeChartPath.replace(
      ':_id',
      encodeURIComponent(familyId)
    ),
    search: `?${query.toString()}`,
    state: { selectedFamilyMemberId: familyMemberId }
  };
}

/** The pedigree with one of the person's Tests open (a linked report's Test). */
export function testLocation({ familyId, familyMemberId, testId }) {
  const query = new URLSearchParams({ familyMemberId, testId });
  return {
    pathname: CONSTANTS.pedigreeChartPath.replace(
      ':_id',
      encodeURIComponent(familyId)
    ),
    search: `?${query.toString()}`,
    state: { selectedFamilyMemberId: familyMemberId }
  };
}

/**
 * What "Create a family from this report" fills in, for a person to edit: the first printed name
 * and the printed sex as a gender identity of Male or Female (the create form has no sex field of
 * its own). The date of birth is not here: the form cannot take one.
 */
export function prefillFromReport(reportIdentity) {
  const name = reportIdentity?.names?.[0] || {};
  const sex = reportIdentity?.sex?.value;
  return {
    firstName: (name.given || []).filter(hasText).join(' '),
    lastName: hasText(name.surname) ? name.surname : '',
    genderIdentity: sex === 'male' ? 'Male' : sex === 'female' ? 'Female' : ''
  };
}

// ── One person of a report about several (TGCLOUD-1868, Phase E2 and E3) ────────────────────────
//
// An individual is `{ key, role, relatedTo, state, ... }` (GET /inbox/:id `individuals`). Its KEY is
// the role where the report has one person in it (`fetus`, `mother`, `father`, `proband`), a
// sibling, a child or a fetus by its place among them (`sibling-2`; twins are `fetus-1` and
// `fetus-2`, TGCLOUD-1868 decision 23), else its place among all of them (`row-3`), so the key's
// number is shown and the key itself never is.

const ROLE_KEYS = Object.freeze(['fetus', 'mother', 'father', 'proband']);
const NUMBERED_KEY = /^(sibling|child|fetus|row)-(\d+)$/;

// The role an individual has: its own, else the one its key is (a `row-3` is none).
const roleOf = (individual) => {
  if (individual?.role) return individual.role;
  if (ROLE_KEYS.includes(individual?.key)) return individual.key;
  const numbered = NUMBERED_KEY.exec(individual?.key || '');
  return numbered && numbered[1] !== 'row' ? numbered[1] : null;
};

/** Whether an individual is a fetus: the role decides, as the server's does (a twin's key is a row). */
export const isFetusIndividual = (individual) => roleOf(individual) === 'fetus';

/**
 * Whether an item's people are a SINGLE-FETUS report's (TGCLOUD-1868, decision 32): one individual,
 * its fetus, keyed `fetus` (backend inboxIndividuals.isSingleFetusItem, the same rule). Such an item
 * is matched as a trio's fetus is, and words meant for a report about several people are said for its
 * one fetus. An item about one person has no individuals, and a single-fetus item stored before
 * decision 32 is one of those: it is worked whole, as it always was.
 */
export const isSingleFetusItem = (individuals) =>
  Array.isArray(individuals) &&
  individuals.length === 1 &&
  individuals[0]?.key === 'fetus' &&
  isFetusIndividual(individuals[0]);

// "Create a family from this report" for one person of a report about several (TGCLOUD-1868, Phase E
// routine call): offered for a mother, a father or a proband, the people a family is made for, and
// never for a fetus, whose record is a fetus record of its mother's pregnancy.
const FAMILY_ROLES = Object.freeze(['mother', 'father', 'proband']);

/** Whether "Create a family from this report" is offered for this person of the report. */
export const offersCreateFamily = (individual) =>
  FAMILY_ROLES.includes(roleOf(individual));

/** An individual as a heading or a chip names them: "Mother", "Sibling 2", "Person 3". */
export function individualLabel(individual) {
  const role = roleOf(individual);
  const word = role
    ? labelOf(INDIVIDUAL_ROLE_LABELS, role)
    : translate('Person');
  const numbered = NUMBERED_KEY.exec(individual?.key || '');
  if (!numbered) return word;
  const [, kind, n] = numbered;
  if (kind !== 'row') return `${word} ${n}`;
  return !role || role === 'other'
    ? translate('Person {{n}}', { n })
    : translate('{{role}} (person {{n}})', { role: word, n });
}

/** An individual inside a sentence: "the mother", "sibling 2", "person 3". */
export function individualNoun(individual) {
  const numbered = NUMBERED_KEY.exec(individual?.key || '');
  if (numbered) {
    const [, kind, n] = numbered;
    return kind === 'row'
      ? translate('person {{n}}', { n })
      : `${translate(kind)} ${n}`;
  }
  const role = roleOf(individual);
  return role
    ? translate('the {{role}}', {
        role: labelOf(INDIVIDUAL_ROLE_LABELS, role).toLowerCase()
      })
    : translate('this person');
}

/** The individual a "Fetus of ..." cell names, when they are on the report too: a sentence. */
export function relatedToText(individual, individuals = []) {
  if (!individual?.relatedTo) return '';
  const other = individuals.find((x) => x.key === individual.relatedTo);
  if (!other) return '';
  return translate('On the report as the {{role}} of {{other}}.', {
    role: individualLabel(individual).toLowerCase(),
    other: individualNoun(other)
  });
}

// A mother's name as recorded, inside a sentence.
const motherNameOf = (candidate) =>
  [candidate?.firstName, candidate?.lastName].filter(hasText).join(' ') ||
  translate('someone with no name recorded');

// The pregnancy's date of record (decision 26), as the matcher sends it: an ISO day, read by its
// calendar parts. The column itself is a `type: Date` the pregnancy editor's picker writes (CLAUDE.md,
// Dates: a local-anchored instant), so an instant, were one sent, is read by its local parts.
// toLocalDateFromInstant reads both. '' when there is none.
const recordedOnText = (pregnancy, fmtDate) =>
  fmtDate(toLocalDateFromInstant(pregnancy?.dateOfRecord)) || '';

// "Fetus n of N" (decision 26): the fetus's place among its pregnancy's fetus records, both as the
// server counts them (1-based). Null unless both are given and agree: a number worked out here from
// the order the candidates come in would be a guess, and could name the wrong twin.
function fetusPositionOf(pregnancy) {
  const n = Number(pregnancy?.fetusPosition);
  const count = Number(pregnancy?.fetusCount);
  return Number.isInteger(n) && Number.isInteger(count) && n >= 1 && n <= count
    ? { n, count }
    : null;
}

const weeksText = (weeks) =>
  weeks === 1
    ? translate('1 week')
    : translate('{{count}} weeks', { count: weeks });

// The pregnancy as its record identifies it: its date of record (decision 26), else its due date,
// else the weeks recorded. EDD is a `type: Date` column only the pregnancy editor's picker writes, so
// it is a local-anchored instant (CLAUDE.md, Dates): read by its local parts.
function pregnancyPhrase(pregnancy, fmtDate) {
  const recorded = recordedOnText(pregnancy, fmtDate);
  if (recorded)
    return translate('the pregnancy recorded on {{date}}', { date: recorded });
  const due = fmtDate(toLocalDateFromInstant(pregnancy?.edd));
  if (due) return translate('the pregnancy due {{date}}', { date: due });
  const weeks = Number(pregnancy?.gestationalWeeks);
  if (Number.isFinite(weeks) && weeks > 0) {
    return translate('the pregnancy at {{weeks}}', { weeks: weeksText(weeks) });
  }
  return translate('a pregnancy');
}

/**
 * A fetus individual's candidate, which is a FETUS RECORD of a pregnancy of its mother, named by the
 * fetus, its pregnancy and its mother (decision 26: "Fetus n of N" and the pregnancy's date of
 * record): "Fetus 1 of 2 in the pregnancy recorded on 12/03/2026, for Imelda Vantress". The fetus's
 * own name, else the symbol text the clinician numbered it with, follows in brackets (as the
 * pedigree names a fetus: fetusEventSubject.fetusDisplayName). A candidate whose place the server
 * does not give is named as before: "Twin B of the pregnancy due 03/03/2027, for Imelda Vantress".
 * PURE: the date formatter is passed in (CLAUDE.md, Dates).
 */
export function fetusCandidateLabel(candidate, fmtDate) {
  const own = [candidate?.fetus?.name, candidate?.fetus?.pedigreeSymbolText]
    .filter(hasText)
    .map((v) => v.trim())[0];
  const pregnancy = pregnancyPhrase(candidate?.pregnancy, fmtDate);
  const mother = motherNameOf(candidate);
  const position = fetusPositionOf(candidate?.pregnancy);
  const vars = { ...position, own, pregnancy, mother };
  if (position) {
    return own
      ? translate(
          'Fetus {{n}} of {{count}} ({{own}}) in {{pregnancy}}, for {{mother}}',
          vars
        )
      : translate(
          'Fetus {{n}} of {{count}} in {{pregnancy}}, for {{mother}}',
          vars
        );
  }
  return own
    ? translate('{{own}} of {{pregnancy}}, for {{mother}}', vars)
    : translate('Fetus of {{pregnancy}}, for {{mother}}', vars);
}

/**
 * What else tells one fetus record from another: what of the pregnancy its name did not use (the
 * due date and the weeks), completed, hidden, a known sex.
 */
export function fetusRecordDetails(candidate, fmtDate) {
  const pregnancy = candidate?.pregnancy || {};
  const fetus = candidate?.fetus || {};
  const weeks = Number(pregnancy.gestationalWeeks);
  const due = fmtDate(toLocalDateFromInstant(pregnancy.edd));
  const recorded = !!recordedOnText(pregnancy, fmtDate);
  return [
    // The due date, when the date of record named the pregnancy.
    recorded && due ? translate('Due {{date}}', { date: due }) : null,
    // The weeks, when a date named the pregnancy (else the weeks did).
    (recorded || !!due) && Number.isFinite(weeks) && weeks > 0
      ? weeksText(weeks)
      : null,
    pregnancy.completed === true ? translate('Completed pregnancy') : null,
    pregnancy.hidden === true ? translate('Not drawn on the pedigree') : null,
    fetus.gender === 'male' || fetus.gender === 'female'
      ? sexText(fetus.gender)
      : null
  ].filter(Boolean);
}

/** A fetus record a decision or a match names, when nothing more is known of it than its mother. */
export const fetusRecordText = (person) =>
  translate('Fetus of a pregnancy, for {{mother}}', {
    mother: motherNameOf(person)
  });

/** The same fetus record: its mother, its pregnancy and itself. */
export const sameFetusRecord = (a, b) =>
  !!a &&
  !!b &&
  String(a.personId) === String(b.personId) &&
  String(a.pregnancyId) === String(b.pregnancyId) &&
  String(a.fetusId) === String(b.fetusId);

/**
 * What a request about one individual's candidate carries beside the person: the individual, and a
 * fetus record's pregnancy and fetus, or, for a person who ties as a single-fetus report's mother,
 * that it names her AS THE MOTHER (`asMother`; TGCLOUD-1868 closeout review). Nothing for a report
 * about one person.
 */
export function individualRequestOf(individualKey, candidate) {
  if (!hasText(individualKey)) return {};
  return {
    individualKey,
    ...(candidate?.pregnancyId != null && candidate?.fetusId != null
      ? {
          pregnancyId: String(candidate.pregnancyId),
          fetusId: String(candidate.fetusId)
        }
      : {}),
    ...(candidate?.asMother === true ? { asMother: true } : {})
  };
}

/** A person rejected as a single-fetus report's mother, as a line: "Imelda Vantress (G-...), as the mother". */
export const asTheMotherText = (person) =>
  person
    ? translate('{{person}}, as the mother', {
        person: `${personName(person)}${
          person.familyMemberId ? ` (${person.familyMemberId})` : ''
        }`
      })
    : translate('A person who no longer exists in this group, as the mother');

// ── Evidence ────────────────────────────────────────────────────────────────────────────────────

/**
 * Whether a candidate's conflict is the REPORT's own: it prints this person two ways (TGCLOUD-1868,
 * decision 25; an evidence item's `detail` 'printed-differently'), which no edit of the record ends.
 */
export const conflictIsTheReports = (evidence = []) =>
  Array.isArray(evidence) &&
  evidence.some((e) => e && e.detail === 'printed-differently');

/** Split a candidate's evidence into the items to show and those the report does not print. */
export function evidenceRows(evidence = []) {
  const items = Array.isArray(evidence) ? evidence.filter(Boolean) : [];
  return {
    shown: items.filter((e) => e.outcome !== 'not-on-report'),
    notOnReport: items
      .filter((e) => e.outcome === 'not-on-report')
      .map((e) => e.item)
  };
}

const dateOrText = (value, fmtDate) =>
  (typeof value === 'string' || value instanceof Date) && fmtDate(value)
    ? fmtDate(value)
    : value == null
    ? ''
    : String(value);

// THE RELATIONSHIP ITEM (TGCLOUD-1868, Phase E3; inboxMatcher.relationshipEvidence) is the outcome
// of each relationship the report states for one of its people (`checks`), the deciding one first
// in `relation` and `detail`. It compares no printed value, so each side is said in words: what the
// report states, and what the records show.
const relationshipChecksOf = (e) => {
  const checks = Array.isArray(e?.checks) ? e.checks.filter(Boolean) : [];
  const decisive = e?.relation
    ? [{ relation: e.relation, detail: e.detail }]
    : [];
  return [...decisive, ...checks];
};

const uniqueWords = (words) => [...new Set(words.filter(hasText))];
const lowerFirst = (s) => s.charAt(0).toLowerCase() + s.slice(1);

/** The report's side of an evidence item, as text. */
export function reportValueText(e, fmtDate) {
  if (e?.item === 'relationship') {
    const words = uniqueWords(
      relationshipChecksOf(e).map((c) =>
        RELATIONSHIP_STATED_LABELS[c.relation]
          ? translate(RELATIONSHIP_STATED_LABELS[c.relation])
          : null
      )
    );
    if (words.length < 2) return words[0] || '';
    return translate('{{list}} and {{last}}', {
      list: words.slice(0, -1).join(', '),
      last: lowerFirst(words[words.length - 1])
    });
  }
  if (!e || e.report == null || e.report === '') return '';
  if (e.item === 'life-status') {
    return translate('Collected {{date}}', {
      date: dateOrText(e.report, fmtDate)
    });
  }
  const value = String(e.report);
  if (
    (e.item === 'national-id' || e.item === 'hospital-number') &&
    hasText(e.labelAsPrinted)
  ) {
    return `${value} (${e.labelAsPrinted})`;
  }
  return value;
}

/** The record's side of an evidence item, as text. */
export function recordValueText(e, fmtDate) {
  if (!e) return '';
  switch (e.item) {
    case 'dob':
      return e.record ? dateOrText(e.record, fmtDate) : '';
    case 'sex':
      return sexText(e.record);
    case 'life-status':
      if (e.record === 'alive') return translate('Alive');
      return e.record
        ? translate('Died {{date}}', { date: dateOrText(e.record, fmtDate) })
        : '';
    case 'report-key':
      return Array.isArray(e.kinds) && e.kinds.length
        ? translate('On an earlier report of this person ({{kinds}})', {
            kinds: e.kinds
              .map((k) => labelOf(REPORT_KEY_LABELS, k).toLowerCase())
              .join(', ')
          })
        : '';
    case 'specimen-id':
      return e.outcome === 'agrees'
        ? translate('This person’s sample')
        : e.outcome === 'disagrees'
        ? translate('Another person’s sample')
        : '';
    case 'relationship':
      return uniqueWords(
        relationshipChecksOf(e).map((c) =>
          RELATIONSHIP_RECORD_LABELS[c.detail]
            ? translate(RELATIONSHIP_RECORD_LABELS[c.detail])
            : null
        )
      )
        .map((w, i) => (i === 0 ? w : lowerFirst(w)))
        .join('; ');
    default:
      return e.record == null ? '' : String(e.record);
  }
}

/**
 * The notes under an evidence item: why it could not be compared, which way round a date agreed,
 * and why a Healthcare ID that agrees counts only as supporting evidence.
 */
export function evidenceNotes(e, { healthcareIdScheme } = {}) {
  if (!e) return [];
  const notes = [];
  // Every relationship the report states says how it was checked, the deciding one first.
  const details =
    e.item === 'relationship'
      ? uniqueWords(relationshipChecksOf(e).map((c) => c.detail))
      : [e.detail];
  for (const detail of details) {
    if (detail && EVIDENCE_DETAIL_LABELS[detail]) {
      notes.push(translate(EVIDENCE_DETAIL_LABELS[detail]));
    }
  }
  // The report printing this person two ways (TGCLOUD-1868, decision 25): whether the record agrees
  // with either printing, as the matcher says (`agreesWithOne`), since that is why they are proposed.
  if (e.detail === 'printed-differently' && e.agreesWithOne === true) {
    notes.push(translate('The record agrees with one of the two.'));
  }
  if (e.item === 'dob' && e.outcome === 'agrees' && e.reading === 'mdy') {
    notes.push(
      e.readings === 2
        ? translate(
            'Agrees only when the printed date is read month first, so it counts as weak evidence.'
          )
        : translate(
            'Agrees read month first, the only way the printed date can be read.'
          )
    );
  }
  if (e.item === 'national-id') {
    if (e.checkDigitValid === false) {
      notes.push(translate('The report’s number fails its check digit.'));
    }
    if (e.outcome === 'agrees' && e.strength === 'supporting') {
      let why;
      if (!healthcareIdScheme) {
        why = translate(
          'this group has not declared what its Healthcare ID field holds (Healthcare ID scheme, on the inbox page).'
        );
      } else if (e.scheme && e.scheme !== healthcareIdScheme) {
        why = translate(
          'the report’s number reads as a {{scheme}}, and this group’s Healthcare ID field holds a {{declared}}.',
          {
            scheme: labelOf(HEALTHCARE_ID_SCHEME_LABELS, e.scheme),
            declared: labelOf(HEALTHCARE_ID_SCHEME_LABELS, healthcareIdScheme)
          }
        );
      } else if (e.recordCheckDigitValid === false) {
        why = translate('the record’s number fails its check digit.');
      } else if (e.detail === 'card-only') {
        why = translate(
          'everyone on a Medicare card shares its number, and only the card with the same IRN names one person.'
        );
      } else {
        why = translate('it cannot be checked as an identifier.');
      }
      notes.push(translate('Supporting evidence only: {{why}}', { why }));
    }
    if (e.outcome === 'agrees' && e.strength === 'identifier') {
      notes.push(
        translate(
          'A checked number of the scheme this group declared: identifier evidence.'
        )
      );
    }
  }
  if (e.item === 'hospital-number' && hasText(e.issuerAsPrinted)) {
    notes.push(
      translate('Issued by {{issuer}}, as the report prints it.', {
        issuer: e.issuerAsPrinted
      })
    );
  }
  return notes;
}
